import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import axios from '../../Actions/axios.config';

export const qaKeys = {
    all: ['qa'],
    lists: () => [...qaKeys.all, 'list'],
    list: (filters) => [...qaKeys.lists(), { filters }],
    details: () => [...qaKeys.all, 'detail'],
    detail: (id) => [...qaKeys.details(), id],
    comments: (postId) => [...qaKeys.all, 'comments', postId],
};

export const useQAPosts = () => {
    return useInfiniteQuery({
        queryKey: qaKeys.lists(),
        queryFn: async ({ pageParam = 1 }) => {
            const { data } = await axios.get(`/qa/posts?page=${pageParam}&limit=10`);
            return data;
        },
        getNextPageParam: (lastPage) => {
            return lastPage.hasNextPage ? parseInt(lastPage.page) + 1 : undefined;
        },
    });
};

export const useQAPost = (postId) => {
    return useQuery({
        queryKey: qaKeys.detail(postId),
        queryFn: async () => {
            const { data } = await axios.get(`/qa/posts/${postId}`);
            return data;
        },
        enabled: !!postId && postId !== 'undefined' && typeof postId === 'string' && postId.length > 10,
    });
};

export const useQAComments = (postId) => {
    return useQuery({
        queryKey: qaKeys.comments(postId),
        queryFn: async () => {
            const { data } = await axios.get(`/qa/posts/${postId}/comments`);
            return data.comments;
        },
        enabled: !!postId && postId !== 'undefined' && typeof postId === 'string' && postId.length > 10,
    });
};

export const useCreateQAPost = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (newPost) => axios.post('/qa/posts', newPost),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qaKeys.lists() });
        },
    });
};

export const useUpdateQAPost = (postId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (updatedPost) => axios.put(`/qa/posts/${postId}`, updatedPost),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qaKeys.lists() });
            queryClient.invalidateQueries({ queryKey: qaKeys.detail(postId) });
        },
    });
};

export const useDeleteQAPost = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (postId) => axios.delete(`/qa/posts/${postId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qaKeys.lists() });
        },
    });
};

export const useToggleQAPostUpvote = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({postId}) => axios.post(`/qa/posts/${postId}/upvote`),
        onSuccess: (variables) => {
            const { postId } = variables;
            queryClient.invalidateQueries({ queryKey: qaKeys.lists() });
            queryClient.invalidateQueries({ queryKey: qaKeys.detail(postId) });
        },
    });
};

export const useCreateQAComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ postId, content }) => axios.post(`/qa/posts/${postId}/comments`, { content }),
        onSuccess: (data, variables) => {
            const { postId } = variables;
            queryClient.invalidateQueries({ queryKey: qaKeys.comments(postId) });
            queryClient.invalidateQueries({ queryKey: qaKeys.detail(postId) });
            queryClient.invalidateQueries({ queryKey: qaKeys.lists() });
        },
    });
};

export const useUpdateQAComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ commentId, content }) => axios.put(`/qa/comments/${commentId}`, { content }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qaKeys.all });
        },
    });
};

export const useDeleteQAComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (commentId) => axios.delete(`/qa/comments/${commentId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qaKeys.all });
        },
    });
};

export const useToggleQACommentUpvote = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (commentId) => axios.post(`/qa/comments/${commentId}/upvote`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qaKeys.all });
        },
    });
};